import { isAfter, startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { isFutureEffectiveDate } from "../../business-logic/billingLogic";
import { getPreapprovedSuffix } from "../../components/ServiceBuilder/ServiceBuilder.utils";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../models";
import { formatShortMonthUSDate } from "./..";
import { utils } from '..';
import { pluralize } from "../pluralize";
export const billingBadgesTextsPath = 'billing.badges';
export const ServiceDescriptionBadgesTestIds = pathgen('ServiceDescriptionBadgesTestIds');
function optionalBadge(service, t) {
  return serviceModel.isOptional(service.participation) ? {
    skin: 'warning',
    intent: 'optional',
    text: t.optional.text(),
    tooltip: t.optional.tooltip()
  } : null;
}
function pausedBadge(service, t) {
  return serviceBillingModel.isPaused(service.billing) ? {
    skin: 'warning',
    intent: 'paused',
    text: t.paused.text(),
    tooltip: t.paused.tooltip()
  } : null;
}
function proratedBadge(service, t) {
  return serviceBillingModel.isProrated(service.billing) ? {
    skin: 'warning',
    intent: 'prorated',
    text: t.prorated.text(),
    tooltip: t.prorated.tooltip()
  } : null;
}
function preapprovedBadge(service, PREAPPROVED_AMOUNT_IMPROVMENTS, t) {
  const {
    cost
  } = service;
  const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing) || 'onetime';
  const suffix = getPreapprovedSuffix(recurrencePeriod);
  if (!serviceCostModel.isVariablePrice(cost) || !cost.unitCap || !cost.unitCap.gt(0)) {
    return null;
  }
  const count = utils.formatNumberWithCommas(cost.unitCap);
  const unit = pluralize(serviceCostModel.formattedUnitName(cost), cost.unitCap.toNumber());
  const text = suffix && PREAPPROVED_AMOUNT_IMPROVMENTS ? t.preapprovedWithPeriod({
    count,
    unit,
    period: suffix.substring(1)
  }) : t.preapproved({
    count,
    unit
  });
  return {
    skin: 'success',
    intent: 'preapproved',
    text
  };
}
export function discountBadge(service, t) {
  const activeDiscount = serviceModel.discount(service);
  const expiredDiscount = serviceModel.expiredDiscount(service);
  const discount = activeDiscount || expiredDiscount;
  if (!discount || !discount.amount().gt(0)) {
    return null;
  }
  const expiration = discount.expiration();
  const isActiveDiscount = !!activeDiscount && (expiration === null || expiration === void 0 ? void 0 : expiration.isActive());
  const isExpiredDiscount = !!expiredDiscount;
  if (isActiveDiscount && expiration) {
    const expirationDate = expiration.date();
    const expirationPeriod = expiration.period();
    const isExpirationPeriodType = expiration.isExpirationPeriodType();
    if (expirationDate) {
      return {
        skin: 'success',
        intent: "limited-".concat(discount.type(), "-discount"),
        text: t.discount.limitedDate({
          amount: discount.format(),
          date: formatShortMonthUSDate(expirationDate)
        })
      };
    }
    if (isExpirationPeriodType && expirationPeriod) {
      const discountAmount = discount.format();
      const count = expiration.amount();
      return {
        skin: 'success',
        intent: "limited-".concat(discount.type(), "-discount"),
        text: t.discount.limitedPeriod[expirationPeriod]({
          amount: discountAmount,
          count
        })
      };
    }
  }
  if (isExpiredDiscount && expiration) {
    return {
      skin: 'success',
      intent: "expired-".concat(discount.type(), "-discount"),
      text: t.discount.expired({
        amount: discount.format()
      })
    };
  }
  return {
    skin: 'success',
    intent: "".concat(discount.type(), "-discount"),
    text: t.discount.standard({
      amount: discount.format()
    })
  };
}
function limitedTimesBadge(service, t) {
  return serviceModel.maxChargesEnabled(service) ? {
    skin: 'warning',
    intent: 'limit',
    text: t.canBeBilled({
      limit: serviceBillingModel.formatMaxChargesLimit(service.billing)
    }),
    tooltip: t.limitedCharges.tooltip()
  } : null;
}
function priceIncreaseBadge(service, t) {
  return serviceModel.priceIncrease(service).exists() ? {
    skin: 'warning',
    intent: 'price-increase',
    text: t.priceIncrease()
  } : null;
}
export function billingDetailsBadge(service, options) {
  const {
    billing
  } = service;
  const {
    t,
    effectiveDate = null,
    now,
    isClientPreview,
    isActiveAgreement
  } = options;
  const getRecurringTextForClientPreview = period => {
    const maxCharges = serviceModel.maxChargesEnabled(service) ? serviceModel.maxChargesLimit(service) : null;
    return maxCharges ? t.billedRecurringWithTimes({
      period,
      times: maxCharges
    }) : t.billedRecurring({
      period
    });
  };
  return serviceBillingModel.accept(billing, {
    visitOnApproval: () => {
      if (isFutureEffectiveDate(effectiveDate, now)) {
        return {
          text: t.billedOnce.onEffectiveDate(),
          intent: 'type-effective-date'
        };
      } else if (isActiveAgreement) {
        return {
          text: t.billedOnce.immediately(),
          intent: 'type-approval-amendment'
        };
      }
      return {
        text: t.billedOnce.onProposalAcceptance(),
        intent: 'type-approval'
      };
    },
    visitOneTimeManual: () => ({
      text: isClientPreview ? t.billedOnce.base() : t.billedOnce.manually(),
      intent: 'type-onetime-manual'
    }),
    visitRepeatableManual: billing => ({
      text: isClientPreview ? getRecurringTextForClientPreview(billing.recurrencePeriod) : t.billedRecurringManually({
        period: billing.recurrencePeriod
      }),
      intent: 'type-repeatable-manual'
    }),
    visitOneTimeInStages: billing => ({
      text: t.billedInStages({
        count: billing.oneTimeInStages.numStages()
      }),
      intent: 'type-one-time-in-stages'
    }),
    visitOngoing: billing => ({
      text: isClientPreview ? getRecurringTextForClientPreview(billing.recurrencePeriod) : t.billedRecurringAutomatically({
        period: billing.recurrencePeriod
      }),
      intent: 'type-ongoing'
    })
  });
}
function initialBillingBadge(service, agreementData, now, isFreshlyAdded, featureFlags, t) {
  const {
    billing
  } = service;
  let isBillOnPeriodBeforeNextBilling = false;
  if (featureFlags.START_BILLING_AT || featureFlags.START_BILLING_AT_PHASE_2_INLINE) {
    isBillOnPeriodBeforeNextBilling = serviceModel.isBillOnPeriodBeforeNextBilling(service);
  } else {
    isBillOnPeriodBeforeNextBilling = serviceBillingModel.isOngoing(billing) && serviceBillingModel.isBilledUpfront(billing) && !serviceBillingModel.isSkipBillingOnAcceptance(billing);
  }
  if (isBillOnPeriodBeforeNextBilling) {
    const effectiveDate = agreementData.effectiveDate;
    const isActiveAgreement = agreementData.isActiveAgreement();
    if ((featureFlags.START_BILLING_AT || featureFlags.START_BILLING_AT_PHASE_2_INLINE) && serviceModel.startBillingAt(service)) {
      return null;
    }
    if (isActiveAgreement) {
      if (isFreshlyAdded) {
        return {
          skin: 'warning',
          intent: 'bill-immediately',
          text: t.chargedImmediately.text(),
          tooltip: t.chargedImmediately.tooltip()
        };
      }
      return null;
    } else {
      if (isFutureEffectiveDate(effectiveDate, now)) {
        return {
          skin: 'warning',
          intent: 'bill-on-effective-date',
          text: t.chargedOnEffectiveDate.text(),
          tooltip: t.chargedOnEffectiveDate.tooltip()
        };
      }
      return {
        skin: 'warning',
        intent: 'bill-on-approval',
        text: t.chargedOnAcceptance.text(),
        tooltip: t.chargedOnAcceptance.tooltip()
      };
    }
  } else {
    return null;
  }
}
function conditionalDiscountBadge(service, t) {
  const expiration = serviceModel.limitedTimeDiscountOfferExpiration(service);
  if (!expiration) {
    return null;
  }
  return {
    skin: 'success',
    intent: 'conditional-discount',
    text: t.conditionalDiscount()
  };
}
function startBillingAtBadge(service, _ref, t) {
  let {
    context,
    now,
    isFreshlyAdded
  } = _ref;
  const startBillingAt = serviceModel.startBillingAt(service);
  const isAgreement = context === 'agreement';
  if (!startBillingAt) {
    return null;
  }
  if (isAgreement && !isFreshlyAdded && !isAfter(startOfDay(startBillingAt), startOfDay(now))) {
    return null;
  }
  return {
    skin: 'warning',
    intent: 'start-billing-at',
    badgeTestId: ServiceDescriptionBadgesTestIds.StartBillingAt(),
    text: t.billingStartsOn({
      date: formatShortMonthUSDate(startBillingAt)
    })
  };
}
function bundleBadge(service, SERVICE_TEMPLATE_BUNDLE, t) {
  return serviceModel.isBundle(service) && SERVICE_TEMPLATE_BUNDLE ? {
    intent: 'bundle',
    text: t.bundle()
  } : null;
}
export function getServiceDescriptionBadges(service, agreementData, now) {
  let featureFlags = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  let {
    t,
    hideBillingDetailsBadge = false,
    hideDiscountBadge = false,
    hidePreapprovedBadge = false,
    hideProratedBadge = false,
    hidePausedBadge = false,
    hideOptionalBadge = false,
    hideLimitedTimesBadge = false,
    hideInitialBillingBadge = false,
    hidePriceIncreaseBadge = false,
    hideConditionalDiscountBadge = false,
    hideBundleBadge = false,
    isFreshlyAdded = false,
    context = 'draft'
  } = arguments.length > 4 ? arguments[4] : undefined;
  return compact([!hideBundleBadge && bundleBadge(service, featureFlags.SERVICE_TEMPLATE_BUNDLE || false, t), !hideBillingDetailsBadge && billingDetailsBadge(service, {
    t,
    isActiveAgreement: agreementData.isActiveAgreement(),
    effectiveDate: agreementData.effectiveDate,
    now
  }), !hideDiscountBadge && discountBadge(service, t), !hidePreapprovedBadge && preapprovedBadge(service, featureFlags.PREAPPROVED_AMOUNT_IMPROVMENTS || false, t), !hideProratedBadge && proratedBadge(service, t), !hidePausedBadge && pausedBadge(service, t), !hideOptionalBadge && optionalBadge(service, t), !hideLimitedTimesBadge && limitedTimesBadge(service, t), !hideInitialBillingBadge && (featureFlags.START_BILLING_AT_PHASE_2_INLINE ? undefined : initialBillingBadge(service, agreementData, now, isFreshlyAdded, featureFlags, t)), !hidePriceIncreaseBadge && priceIncreaseBadge(service, t), !hideConditionalDiscountBadge && conditionalDiscountBadge(service, t), featureFlags.START_BILLING_AT_PHASE_2_INLINE ? undefined : featureFlags.START_BILLING_AT && startBillingAtBadge(service, {
    now,
    isFreshlyAdded,
    context
  }, t)]);
}